import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
const Chatbot = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([
        {
            id: '0',
            text: 'Γειά σας! Πώς μπορώ να σας βοηθήσω σήμερα;',
            sender: 'bot',
            timestamp: new Date()
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    useEffect(() => {
        scrollToBottom();
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [messages, isOpen]);
    // Lock body scroll when chat is open
    useEffect(() => {
        if (isOpen) {
            // Prevent background scrolling on mobile
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            return () => {
                document.body.style.overflow = '';
                document.documentElement.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.height = '';
            };
        }
    }, [isOpen]);
    const sendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim())
            return;
        // Add user message
        const userMessage = {
            id: Date.now().toString(),
            text: input,
            sender: 'user',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);
        const userInput = input;
        setInput('');
        setIsLoading(true);
        try {
            // Call OUR backend proxy, which will handle all the bot communication details
            // The backend manages CSRF tokens, session cookies, and all bot-specific requirements
            const headers = {
                'Content-Type': 'application/json',
            };
            const payload = {
                message: userInput
            };
            console.log('[Chatbot] Sending message via backend proxy');
            const response = await fetch('/api/chatbot/chat', {
                method: 'POST',
                headers,
                credentials: 'same-origin',
                body: JSON.stringify(payload)
            });
            let botReply = 'Συγγνώμη, υπήρξε σφάλμα κατά την επεξεργασία του μηνύματός σας.';
            if (response.ok) {
                const data = await response.json();
                console.log('[Chatbot] Response:', data);
                botReply = data.response || data.message || botReply;
            }
            else {
                let errorData = {};
                try {
                    errorData = await response.json();
                }
                catch (e) {
                    const text = await response.text();
                    console.error('[Chatbot] Error response (non-JSON):', response.status, text);
                }
                console.error('[Chatbot] Chat error:', response.status, errorData);
                if (response.status === 400) {
                    botReply = 'Σφάλμα αιτήματος. Ο διακομιστής του bot δεν δέχθηκε το μήνυμα.';
                }
                else if (response.status === 403) {
                    botReply = 'Σφάλμα ασφάλειας. Παρακαλώ δοκιμάστε ξανά.';
                }
                else if (response.status === 500 || response.status === 502) {
                    botReply = 'Σφάλμα διακομιστή. Παρακαλώ δοκιμάστε αργότερα.';
                }
            }
            const botMessage = {
                id: (Date.now() + 1).toString(),
                text: botReply,
                sender: 'bot',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, botMessage]);
        }
        catch (error) {
            console.error('[Chatbot] Error sending message:', error);
            const errorMessage = {
                id: (Date.now() + 1).toString(),
                text: 'Δυστυχώς, προέκυψε σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε αργότερα.',
                sender: 'bot',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        }
        finally {
            setIsLoading(false);
        }
    };
    if (!isOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden", onClick: onClose, "aria-hidden": "true" }), _jsxs("div", { className: "fixed left-0 right-0 md:inset-auto md:bottom-8 md:right-0 md:m-4 w-full md:w-96 md:h-[600px] z-[9999] flex flex-col md:rounded-2xl shadow-2xl border border-violet-200/20 bg-background overflow-hidden animate-in slide-in-from-bottom-5", style: {
                    top: window.innerWidth < 768 ? '3.5rem' : undefined,
                    bottom: window.innerWidth < 768 ? '0' : undefined,
                    height: window.innerWidth < 768 ? 'calc(100dvh - 3.5rem)' : undefined
                }, children: [_jsxs("div", { className: "bg-gradient-to-r from-violet-500 to-blue-500 p-4 pr-16 md:pr-4 text-white flex items-center justify-between shrink-0 relative", children: [_jsx("div", { className: "flex items-center gap-3", children: _jsxs("div", { children: [_jsx("h3", { className: "font-bold text-lg", children: "QbCron AI Assistant" }), _jsx("p", { className: "text-xs opacity-90", children: "\u03A0\u03AC\u03BD\u03C4\u03B1 \u03B5\u03B4\u03CE \u03B3\u03B9\u03B1 \u03BD\u03B1 \u03C3\u03B1\u03C2 \u03B2\u03BF\u03B7\u03B8\u03AE\u03C3\u03B5\u03B9" })] }) }), _jsx("button", { onClick: onClose, className: "absolute top-6 right-4 md:static p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-[60] shadow-lg border border-white/20 min-w-[48px] min-h-[48px] flex items-center justify-center", "aria-label": "Close chat", children: _jsx(X, { className: "w-6 h-6 text-white", "aria-hidden": "true" }) })] }), _jsxs("div", { className: "flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain", children: [messages.map(message => (_jsx("div", { className: `flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`, children: _jsxs("div", { className: `max-w-xs px-4 py-2 rounded-lg ${message.sender === 'user'
                                        ? 'bg-violet-500 text-white rounded-br-none'
                                        : 'bg-gray-200 text-gray-900 rounded-bl-none dark:bg-gray-700 dark:text-white'}`, children: [_jsx("p", { className: "text-sm", children: message.text }), _jsx("span", { className: "text-xs opacity-70 mt-1 block", children: message.timestamp.toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) })] }) }, message.id))), isLoading && (_jsx("div", { className: "flex justify-start", children: _jsx("div", { className: "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white px-4 py-2 rounded-lg rounded-bl-none", children: _jsxs("div", { className: "flex gap-1", children: [_jsx("div", { className: "w-2 h-2 bg-gray-500 rounded-full animate-bounce" }), _jsx("div", { className: "w-2 h-2 bg-gray-500 rounded-full animate-bounce", style: { animationDelay: '0.2s' } }), _jsx("div", { className: "w-2 h-2 bg-gray-500 rounded-full animate-bounce", style: { animationDelay: '0.4s' } })] }) }) })), _jsx("div", { ref: messagesEndRef })] }), _jsxs("form", { onSubmit: sendMessage, className: "border-t border-gray-200 dark:border-gray-700 p-4 flex gap-2 shrink-0 bg-background", children: [_jsx("input", { ref: inputRef, type: "text", value: input, onChange: (e) => setInput(e.target.value), onFocus: () => {
                                    // Scroll to bottom when input is focused (keyboard opens)
                                    setTimeout(() => {
                                        scrollToBottom();
                                    }, 300);
                                }, placeholder: "\u03A0\u03BB\u03B7\u03BA\u03C4\u03C1\u03BF\u03BB\u03BF\u03B3\u03AE\u03C3\u03C4\u03B5 \u03C4\u03BF \u03BC\u03AE\u03BD\u03C5\u03BC\u03AC \u03C3\u03B1\u03C2...", className: "flex-1 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500", disabled: isLoading }), _jsx(Button, { type: "submit", disabled: isLoading || !input.trim(), "aria-label": "Send message", className: "bg-violet-500 hover:bg-violet-600 text-white rounded-lg p-3 min-w-[48px] min-h-[48px] flex items-center justify-center", children: _jsx(Send, { className: "w-5 h-5", "aria-hidden": "true" }) })] })] })] }));
};
export default Chatbot;
